import { z } from "zod";
import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js";

export const inventoryLocationKindV1Schema = z.enum([
  "central",
  "branch",
  "vehicle",
  "goods_in_transit",
  "rejected",
  "work_in_progress",
]);

export const inventoryLocationAdminV1Schema = z
  .object({
    requestVersion: z.literal(1),
    correlationId: correlationIdSchema,
    idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
    expectedVersion: z.number().int().nonnegative(),
    locationId: entityIdSchema,
    revisionId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    action: z.enum(["create", "update", "retire"]),
    locationKind: inventoryLocationKindV1Schema,
    code: z.string().trim().min(1).max(140),
    displayName: z.string().trim().min(1).max(240),
    vehicleAssetId: entityIdSchema.nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    if ((value.action === "create") !== (value.expectedVersion === 0)) {
      context.addIssue({
        code: "custom",
        message:
          "Create requires version zero; changes require a current version.",
      });
    }
    if (
      (value.locationKind === "vehicle") !==
      (value.vehicleAssetId !== null)
    ) {
      context.addIssue({
        code: "custom",
        message: "Only a vehicle location requires a Fleet Asset reference.",
      });
    }
  });

export const inventoryLocationResultV1Schema = z
  .object({
    entityType: z.literal("inventory_location"),
    entityId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    state: z.enum(["active", "retired"]),
    version: z.number().int().positive(),
    locationKind: inventoryLocationKindV1Schema,
    warehouse: z.string().min(1).max(240),
    parentWarehouse: z.string().min(1).max(240),
    company: z.string().min(1).max(240),
    costCenter: z.string().min(1).max(240),
    accountingDimension: z.string().min(1).max(240),
    vehicleAssetId: entityIdSchema.nullable(),
    quantityEndpoint: z.literal("leaf"),
  })
  .strict();

export type InventoryLocationAdminV1 = z.infer<
  typeof inventoryLocationAdminV1Schema
>;
